import {
  CreateOrderRequest,
  Experience,
  ExperienceStatus,
  Order,
} from '@/types'
import { Formik, FormikHelpers } from 'formik'
import React, { useState, useRef } from 'react'
import ExperienceBookingForm from '@/components/experiences/experience-booking-form'
import useCart from '@/contexts/useCart'
import { useTranslation } from 'next-i18next'
import { useErrors } from '@/hooks/use-errors'
import {
  findDuplicateCartOrder,
  isFreeExperience,
  isInIframe,
} from '@/helper/utils'
import { useRouter } from 'next/router'
import { OrderService } from '@/lib/services/order'
import { format } from 'date-fns'
import { useToast } from '@/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import AddToCartAnimation from '../animations/add-to-cart'

const ExperienceBooking = ({
  experience,
  bookable = true,
  sellerId,
  afterSubmit,
  redirectToCheckout,
  isQuickBooking,
  isBlocking,
  voucherOrder,
  widgetUuid,
  skipPostSubmitRedirect = false,
  bypassCutoff = false,
}: {
  experience: Experience
  bookable?: boolean
  sellerId: number
  afterSubmit?: () => void
  redirectToCheckout?: () => void
  isQuickBooking?: boolean
  isBlocking?: boolean
  voucherOrder: Order | undefined
  widgetUuid?: string
  /**
   * When `true`, the post-redeem `router.push('/bookings/{id}')` is suppressed.
   * Used by the public /redeem flow (Plan 01-07 / B-03 fix) — anonymous users
   * MUST stay on `/redeem/{groupId}` so the card transitions to "Riscattato"
   * (REDEEM-05). The default `false` preserves the legacy behavior for the
   * authenticated widget flows.
   */
  skipPostSubmitRedirect?: boolean
  /**
   * DEV-730: the operator "Book Now" flow opts in to bypass the experience
   * cutoff window (DEV-526). Defaults to false so public store/widget pages
   * keep enforcing the cutoff even when a logged-in operator's token leaks.
   */
  bypassCutoff?: boolean
}) => {
  const { addToCart, setDrawerOpen, masterOrder } = useCart()
  const { t } = useTranslation('experience')
  const { handleError } = useErrors()
  const router = useRouter()
  const { toast } = useToast()
  const [showAnimation, setShowAnimation] = useState(false)
  const [animationStartElement, setAnimationStartElement] =
    useState<HTMLElement | null>(null)
  const submitButtonRef = useRef<HTMLElement | null>(null)
  const [bookSuccess, setBookSuccess] = useState(false)
  // DEV-801: a pending add that duplicates a cart line, awaiting the guest's
  // "add anyway" confirmation.
  const [duplicatePending, setDuplicatePending] = useState<{
    values: CreateOrderRequest
    helpers: FormikHelpers<CreateOrderRequest>
  } | null>(null)

  const isFree = isFreeExperience(experience)

  // Detect preview mode vs public widget
  const isPreviewMode = router.pathname.includes('/preview/')

  // Check if experience is genuinely unavailable (inactive/draft)
  const isExperienceUnavailable = experience.status !== ExperienceStatus.Active

  const handleSubmitButtonClick = (e: any) => {
    submitButtonRef.current = e.target
  }

  if (experience.isInformationPage && experience.options.length === 0) {
    return null
  }

  // PUBLIC MODE: If experience is unavailable, hide booking form
  // (Warning is shown in ExperienceDetail component)
  if (!isPreviewMode && isExperienceUnavailable) {
    return null
  }

  // Add the line to the cart and give feedback. Split out from `onSubmit` so the
  // DEV-801 duplicate warning can offer an "add anyway" action that re-runs this
  // exact path, bypassing the guard.
  const completeAddToCart = async (
    values: CreateOrderRequest,
    { setSubmitting, setErrors, setFieldValue }: FormikHelpers<CreateOrderRequest>
  ) => {
    setSubmitting(true)
    try {
      const createdMasterOrder = await addToCart({
        ...values,
        isQuickBooking,
        // DEV-730: tell the backend this is the operator Book Now flow so
        // the server-side cutoff guard is skipped. Omitted (false) for
        // public store/widget checkout.
        book_now: bypassCutoff,
      })

      afterSubmit?.()

      if (voucherOrder) {
        // B-03 / REDEEM-05: Public /redeem flow opts out of the redirect — anonymous
        // users would be bounced to /auth/login by the admin middleware, breaking the
        // landing-page card-flip. The dialog's `afterSubmit` (above) already triggered
        // a refetch + close; staying on `/redeem/{groupId}` is the intended UX.
        if (!skipPostSubmitRedirect) {
          router.push('/bookings/' + createdMasterOrder.orders[0].id)
        }
        return
      }

      if (isQuickBooking) {
        router.push('/create-order/checkout')
        return
      } else if (isFree && redirectToCheckout) {
        redirectToCheckout?.()
        return
      }

      // Embedded (iframe) widgets have no slide-over cart drawer — their cart is
      // a dedicated page (see AppNavbar) — so keep the fly-to-cart animation as
      // the "added" cue; elsewhere open the cart drawer. The DEV-801 duplicate
      // confirmation (below) is what actually prevents the double charge.
      if (isInIframe()) {
        const button = document.querySelector(
          '#add-to-cart-button'
        ) as HTMLElement

        setAnimationStartElement(button)
        setShowAnimation(true)
        setBookSuccess(true)
      } else {
        setTimeout(() => {
          setDrawerOpen(true)
        }, 100)
      }

      setFieldValue('selectedPrices', {})
    } catch (error: any) {
      handleError(error, setErrors)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Formik<CreateOrderRequest>
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          selectedOption: undefined,
          selectedDate: undefined,
          startsSlot: undefined,
          selectedPrices: {},
          selectedPoint: experience.meetingPoint,
          source: router.pathname.startsWith('app') ? 'online' : 'admin',
          sellerId,
          isBuyingVoucher: experience.onlyBuyAsVoucher ?? false,
          widgetUuid,
        }}
        onSubmit={async (values, helpers) => {
          const { setSubmitting, setErrors } = helpers

          if (!bookable) {
            // Show appropriate message based on context
            const message = isPreviewMode
              ? t('youre_in_preview_mode')
              : t('experience_not_available_for_booking')

            toast({
              title: message,
              variant: 'destructive',
            })
            return
          }

          if (isBlocking) {
            setSubmitting(true)
            try {
              await OrderService.blockBooking({
                experienceId: experience.id,
                selectedDate: format(values.selectedDate!, 'yyyy-MM-dd'),
                startsSlot: values.startsSlot!,
                sellerId,
                optionId: values.selectedOption!,
                selectedPrices: values.selectedPrices,
              })

              afterSubmit?.()

              toast({
                title: t('booking_blocked'),
                variant: 'success',
              })
            } catch (error: any) {
              handleError(error, setErrors)
            } finally {
              setSubmitting(false)
            }
            return
          }

          // DEV-801: warn before adding a line that duplicates one already in the
          // cart (same option, day and slot). The embedded widget's weak "added"
          // feedback led guests to re-add the same booking and pay twice. They can
          // still add it on purpose via the confirmation's "add anyway" action.
          const duplicate = findDuplicateCartOrder(
            masterOrder?.orders,
            values.selectedOption,
            values.selectedDate,
            values.startsSlot
          )

          if (duplicate) {
            // Ask before creating an identical second line — the whole point of
            // DEV-801. The confirmation must be unmissable (not a top-anchored
            // toast a scrolled embedded widget can push off-screen), so use a
            // centered modal; "add anyway" re-runs the add via completeAddToCart.
            // Clear Formik's submit flag on this early return so cancelling the
            // dialog leaves the submit button usable.
            setSubmitting(false)
            setDuplicatePending({ values, helpers })
            return
          }

          await completeAddToCart(values, helpers)
        }}
      >
        {(props) => (
          <ExperienceBookingForm
            {...props}
            experience={experience}
            onSubmitButtonClick={handleSubmitButtonClick}
            bookSuccess={bookSuccess}
            redirectToCheckout={redirectToCheckout}
            voucherOrder={voucherOrder}
            bypassCutoff={bypassCutoff}
          />
        )}
      </Formik>

      {showAnimation && animationStartElement && (
        <AddToCartAnimation
          startElement={animationStartElement}
          onComplete={() => {
            setShowAnimation(false)
            setAnimationStartElement(null)
          }}
        />
      )}

      <AlertDialog
        open={!!duplicatePending}
        onOpenChange={(open) => {
          if (!open) {
            setDuplicatePending(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('already_in_cart')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('already_in_cart_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('cancel', { ns: 'common' })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const pending = duplicatePending
                setDuplicatePending(null)
                if (pending) {
                  completeAddToCart(pending.values, pending.helpers)
                }
              }}
            >
              {t('add_anyway')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default ExperienceBooking
